import React, { useCallback, useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { buildRequestVoucherHtml, buildLiquidationDocumentHtml } from "../../utils/costAnalysisBasisHtml";
import { buildCostAnalysisReportHtml } from "../../utils/costAnalysisReportHtml";
import { buildCheckVoucherPrintHtml } from "../../utils/checkVoucherHtml";
import { API_BASE_URL, authFetch } from "../../config/api";

const getGrossProfitValue = (row) => Number(row.sell_rate || 0) - Number(row.cost_rate || 0);

const ProductionReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);

  // Filters
  const [dateFilter, setDateFilter] = useState("week");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [customDateModalOpen, setCustomDateModalOpen] = useState(false);
  const [customDateDraftStart, setCustomDateDraftStart] = useState("");
  const [customDateDraftEnd, setCustomDateDraftEnd] = useState("");
  const [clientNameFilter, setClientNameFilter] = useState("");
  const [jobOrderFilter, setJobOrderFilter] = useState("");
  const [activeDocumentsRow, setActiveDocumentsRow] = useState(null);
  const [workflowDocsLoading, setWorkflowDocsLoading] = useState(false);
  const [workflowDocsError, setWorkflowDocsError] = useState("");
  const [workflowDocs, setWorkflowDocs] = useState([]);

  // Sorting
  const [sortField, setSortField] = useState("job_order_no");
  const [sortDirection, setSortDirection] = useState("asc");
  const previousDateFilterRef = useRef("all");

  const formatCurrency = (value) => {
    const num = Number(value || 0);
    return num.toLocaleString("en-PH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatPercentage = (value) => {
    const num = Number(value || 0);
    return `${num.toFixed(2)}%`;
  };

  const readJsonResponse = async (res) => {
    const raw = await res.text();
    const contentType = res.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      return JSON.parse(raw);
    }

    try {
      return JSON.parse(raw);
    } catch {
      const preview = raw.trim().slice(0, 120);
      throw new Error(
        preview.startsWith("<!DOCTYPE") || preview.startsWith("<html")
          ? "API returned HTML instead of JSON. Check the backend URL or auth session."
          : "API returned an unexpected non-JSON response."
      );
    }
  };

  const openPrintableHtml = (html) => {
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(html);
    win.document.close();
    win.focus();
  };

  const buildCostAnalysisHtml = (requestData) => {
    const reportData = requestData?.cost_analysis_report || null;
    if (!requestData || !reportData) return "";

    const billingRows = reportData.billing_charge_rows || [];
    const receiptedRows = reportData.receipted_charge_rows || [];
    const actualRows = reportData.actual_cost_rows || [];
    const billingSubtotal = billingRows.reduce(
      (sum, row) => sum + Number(row.computed_amount ?? row.base_amount ?? 0),
      0
    );
    const receiptedSubtotal = receiptedRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const grandTotalValue = billingSubtotal + receiptedSubtotal;
    const actualCostTotalValue = actualRows.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const grossProfitValue = grandTotalValue - actualCostTotalValue;

    return buildCostAnalysisReportHtml({
      request: requestData,
      reportData,
      billingRows,
      receiptedRows,
      actualRows,
      billingSubtotal,
      receiptedSubtotal,
      grandTotalValue,
      actualCostTotalValue,
      grossProfitValue,
      connectedRequests: requestData ? [requestData] : [],
    });
  };

  const buildLiquidationHtml = (requestData) => {
    const reportData = requestData?.liquidation_report || null;
    if (!requestData || !reportData) return "";

    return buildLiquidationDocumentHtml({
      request: requestData,
      reportData,
      shipment: null,
      reportDateCreatedValue: reportData.report_date_created || reportData.created_at || "",
      dockValue: reportData.dock || "",
      poNoValue: reportData.po_no || "",
      referenceNoValue: reportData.reference_no || "",
      deliveryDateValue: reportData.delivery_date || "",
      actualRows: reportData.actual_cost_rows || [],
      advanceRowsValue: reportData.advance_rows || [],
      actualCostTotalValue: reportData.total_actual_cost_incurred || 0,
      advancesTotalValue: reportData.total_advances || 0,
      reimbursementTotalValue: reportData.total_reimbursement_return || 0,
    });
  };

  const buildWorkflowDocs = (requestData, voucherDetails = []) => {
    const sections = [];

    if (requestData) {
      sections.push({
        key: "request",
        title: "Request",
        subtitle: `${requestData.rfp_no || "—"} | ${requestData.job_order || "—"}`,
        html: buildRequestVoucherHtml({ item: requestData, shipment: null }),
      });
    }

    const costAnalysisHtml = buildCostAnalysisHtml(requestData);
    if (costAnalysisHtml) {
      sections.push({
        key: "cost-analysis",
        title: "Cost Analysis",
        subtitle: requestData?.cost_analysis?.status || "Approved request",
        html: costAnalysisHtml,
      });
    }

    const liquidationHtml = buildLiquidationHtml(requestData);
    if (liquidationHtml) {
      sections.push({
        key: "liquidation",
        title: "Liquidation",
        subtitle: requestData?.liquidation?.status || "Liquidation report",
        html: liquidationHtml,
      });
    }

    voucherDetails.forEach((voucher, index) => {
      sections.push({
        key: `voucher-${voucher.id || index}`,
        title: `Voucher ${voucher.voucher_no || index + 1}`,
        subtitle: "Check voucher",
        html: buildCheckVoucherPrintHtml({
          voucher,
          requests: voucher.requests || [],
          lines: voucher.lines || [],
        }),
      });
    });

    return sections;
  };

  const loadWorkflowDocuments = async (row) => {
    if (!row?.request_id) return;

    setWorkflowDocsLoading(true);
    setWorkflowDocsError("");
    setWorkflowDocs([]);

    try {
      const [costAnalysisRes, vouchersRes] = await Promise.all([
        authFetch(`${API_BASE_URL}/api/accounting/cost-analysis/${row.request_id}`),
        authFetch(`${API_BASE_URL}/api/accounting/check-vouchers`),
      ]);

      const costAnalysisJson = await readJsonResponse(costAnalysisRes);
      if (!costAnalysisJson.success) {
        throw new Error(costAnalysisJson.message || "Failed to load cost analysis");
      }

      const vouchersJson = await readJsonResponse(vouchersRes);
      if (!vouchersJson.success) {
        throw new Error(vouchersJson.message || "Failed to load vouchers");
      }

      const linkedVoucherSummaries = (vouchersJson.data || []).filter((voucher) =>
        Array.isArray(voucher.request_ids)
          ? voucher.request_ids.some((requestId) => String(requestId) === String(row.request_id))
          : false
      );

      const voucherDetails = await Promise.all(
        linkedVoucherSummaries.map(async (voucher) => {
          try {
            const detailRes = await authFetch(`${API_BASE_URL}/api/accounting/check-vouchers/${voucher.id}`, {
              credentials: "include",
            });
            const detailJson = await readJsonResponse(detailRes);
            if (!detailJson.success) return null;
            return detailJson.data || null;
          } catch (err) {
            console.error("Failed to load voucher detail", voucher.id, err);
            return null;
          }
        })
      );

      const requestData = costAnalysisJson.data || null;
      setWorkflowDocs(buildWorkflowDocs(requestData, voucherDetails.filter(Boolean)));
    } catch (err) {
      console.error("Load workflow documents error:", err);
      setWorkflowDocsError(err.message || "Failed to load workflow documents");
    } finally {
      setWorkflowDocsLoading(false);
    }
  };

  const openWorkflowDocuments = (row) => {
    setActiveDocumentsRow(row);
    loadWorkflowDocuments(row);
  };

  const buildSummaryFromRows = useCallback((rows = []) => {
    const summary = rows.reduce(
      (acc, row) => {
        acc.totalBillingCharges += Number(row.sell_rate || 0);
        acc.totalActualCosts += Number(row.cost_rate || 0);
        acc.totalGrossProfit += getGrossProfitValue(row);
        acc.count += 1;
        return acc;
      },
      {
        totalBillingCharges: 0,
        totalActualCosts: 0,
        totalGrossProfit: 0,
        count: 0,
      }
    );

    const totalRevenue = summary.totalBillingCharges;
    summary.overallCostPercentage =
      totalRevenue > 0
        ? Math.round((summary.totalActualCosts / totalRevenue) * 10000) / 100
        : 0;
    summary.overallGrossProfitPercentage =
      totalRevenue > 0
        ? Math.round((summary.totalGrossProfit / totalRevenue) * 10000) / 100
        : 0;

    return summary;
  }, []);

  const getDateRange = useCallback((filter) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (filter) {
      case "today":
        return { start: today.toISOString().split("T")[0], end: today.toISOString().split("T")[0] };
      case "week": {
        const dayOfWeek = today.getDay();
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - dayOfWeek);
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return {
          start: startOfWeek.toISOString().split("T")[0],
          end: endOfWeek.toISOString().split("T")[0],
        };
      }
      case "month": {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return {
          start: startOfMonth.toISOString().split("T")[0],
          end: endOfMonth.toISOString().split("T")[0],
        };
      }
      case "custom":
        if (customStartDate && customEndDate) {
          return { start: customStartDate, end: customEndDate };
        }
        return null;
      default:
        return null;
    }
  }, [customEndDate, customStartDate]);

  const getActiveDateRangeLabel = useCallback(() => {
    const dateRange = getDateRange(dateFilter);
    if (!dateRange) return dateFilter === "custom" ? "Custom Range" : "All Time";
    return `${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`;
  }, [dateFilter, getDateRange]);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const url = new URL(`${API_BASE_URL}/api/accounting/production-report`);

      const dateRange = getDateRange(dateFilter);
      if (dateRange) {
        url.searchParams.set("startDate", dateRange.start);
        url.searchParams.set("endDate", dateRange.end);
      }
      if (clientNameFilter.trim()) {
        url.searchParams.set("clientName", clientNameFilter.trim());
      }
      if (jobOrderFilter.trim()) {
        url.searchParams.set("jobOrder", jobOrderFilter.trim());
      }

      const res = await authFetch(url, { credentials: "include" });
      const json = await res.json();

      if (json.success) {
        const reportRows = json.data || [];
        setData(reportRows);
        setSummary(buildSummaryFromRows(reportRows));
      } else {
        setError(json.message || "Failed to load report");
      }
    } catch (err) {
      console.error("Load production report error:", err);
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  }, [buildSummaryFromRows, clientNameFilter, dateFilter, getDateRange, jobOrderFilter]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleSearch = () => {
    loadReport();
  };

  const openCustomDateModal = () => {
    setCustomDateDraftStart(customStartDate);
    setCustomDateDraftEnd(customEndDate);
    setCustomDateModalOpen(true);
  };

  const closeCustomDateModal = () => {
    setCustomDateModalOpen(false);
    if (!customStartDate || !customEndDate) {
      setDateFilter(previousDateFilterRef.current || "all");
    }
  };

  const applyCustomDateRange = () => {
    setCustomStartDate(customDateDraftStart);
    setCustomEndDate(customDateDraftEnd);
    setCustomDateModalOpen(false);
  };

  const handleReset = () => {
    setDateFilter("all");
    setCustomStartDate("");
    setCustomEndDate("");
    setCustomDateModalOpen(false);
    setCustomDateDraftStart("");
    setCustomDateDraftEnd("");
    setClientNameFilter("");
    setJobOrderFilter("");
    previousDateFilterRef.current = "all";
    setTimeout(() => {
      loadReport();
    }, 0);
  };

  // Sorting logic
  const sortedData = [...data].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];

    // Handle numeric fields
    if (["cost_rate", "sell_rate", "gross_profit", "cost_percentage", "gross_profit_percentage", "amount_billed", "amount_collected"].includes(sortField)) {
      aVal = Number(aVal || 0);
      bVal = Number(bVal || 0);
    }

    // Handle date fields
    if (["date_delivered", "liquidation_date", "date_billed"].includes(sortField)) {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }

    // Handle string fields
    if (typeof aVal === "string") aVal = aVal.toLowerCase();
    if (typeof bVal === "string") bVal = bVal.toLowerCase();

    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return "fa-sort";
    return sortDirection === "asc" ? "fa-sort-up" : "fa-sort-down";
  };

  // Export to Excel
  const exportToExcel = () => {
    const exportData = sortedData.map((r) => ({
      "JOB ORDER NO": r.job_order_no || "",
      "BILL OF LADING": r.bill_of_lading || "",
      "CLIENT NAME": r.client_name || "",
      "COST RATE": Number(r.cost_rate || 0),
      "SELL RATE": Number(r.sell_rate || 0),
      "GROSS PROFIT": getGrossProfitValue(r),
      "%COST": Number(r.cost_percentage || 0),
      "%GROSS PROFIT": Number(r.gross_profit_percentage || 0),
      "DATE DELIVERED": r.date_delivered ? formatDate(r.date_delivered) : "",
      "LIQUIDATION DATE": r.liquidation_date ? formatDate(r.liquidation_date) : "",
      "LADING O/R": r.lading_or || "",
      "CHARGES": r.charges || "",
      "SOA/BILLING INVOICE": r.soa_billing_invoice || "",
      "DATE BILLED": r.date_billed ? formatDate(r.date_billed) : "",
      "AMOUNT BILLED": Number(r.amount_billed || 0),
      "OR/AR": r.or_ar || "",
      "AMOUNT COLLECTED": Number(r.amount_collected || 0),
      "BANK DEPOSITED": r.bank_deposited || "",
    }));

    // Add summary row
    if (exportData.length) {
      const totals = sortedData.reduce(
        (acc, r) => {
          acc.costRate += Number(r.cost_rate || 0);
          acc.sellRate += Number(r.sell_rate || 0);
          acc.grossProfit += getGrossProfitValue(r);
          acc.amountBilled += Number(r.amount_billed || 0);
          acc.amountCollected += Number(r.amount_collected || 0);
          return acc;
        },
        { costRate: 0, sellRate: 0, grossProfit: 0, amountBilled: 0, amountCollected: 0 }
      );

      exportData.push({
        "JOB ORDER NO": "TOTALS",
        "BILL OF LADING": "",
        "CLIENT NAME": "",
        "COST RATE": totals.costRate,
        "SELL RATE": totals.sellRate,
        "GROSS PROFIT": totals.grossProfit,
        "%COST": "",
        "%GROSS PROFIT": "",
        "DATE DELIVERED": "",
        "LIQUIDATION DATE": "",
        "LADING O/R": "",
        "CHARGES": "",
        "SOA/BILLING INVOICE": "",
        "DATE BILLED": "",
        "AMOUNT BILLED": totals.amountBilled,
        "OR/AR": "",
        "AMOUNT COLLECTED": totals.amountCollected,
        "BANK DEPOSITED": "",
      });
    }

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Production Report");

    // Generate filename
    let filename = "production_report_";
    if (dateFilter === "today") {
      filename += `today_${new Date().toISOString().split("T")[0]}`;
    } else if (dateFilter === "week") {
      filename += `week_${new Date().toISOString().split("T")[0]}`;
    } else if (dateFilter === "month") {
      const now = new Date();
      filename += `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, "0")}`;
    } else if (dateFilter === "custom" && customStartDate && customEndDate) {
      filename += `${customStartDate}_to_${customEndDate}`;
    } else {
      filename += "all";
    }
    filename += ".xlsx";

    XLSX.writeFile(wb, filename);
  };

  // Calculate totals for footer
    const totals = sortedData.reduce(
    (acc, r) => {
      acc.costRate += Number(r.cost_rate || 0);
      acc.sellRate += Number(r.sell_rate || 0);
      acc.grossProfit += getGrossProfitValue(r);
      acc.amountBilled += Number(r.amount_billed || 0);
      acc.amountCollected += Number(r.amount_collected || 0);
      return acc;
    },
    { costRate: 0, sellRate: 0, grossProfit: 0, amountBilled: 0, amountCollected: 0 }
  );

  const columns = [
    { key: "job_order_no", label: "JOB ORDER NO", sortable: true },
    { key: "bill_of_lading", label: "BILL OF LADING", sortable: true },
    { key: "client_name", label: "CLIENT NAME", sortable: true },
    { key: "cost_rate", label: "COST RATE", sortable: true, type: "currency" },
    { key: "sell_rate", label: "SELL RATE", sortable: true, type: "currency" },
    { key: "gross_profit", label: "GROSS PROFIT", sortable: true, type: "currency" },
    { key: "cost_percentage", label: "%COST", sortable: true, type: "percentage" },
    { key: "gross_profit_percentage", label: "%GROSS PROFIT", sortable: true, type: "percentage" },
    { key: "date_delivered", label: "DATE DELIVERED", sortable: true, type: "date" },
    { key: "liquidation_date", label: "LIQUIDATION DATE", sortable: true, type: "date" },
    { key: "lading_or", label: "LADING O/R", sortable: false },
    { key: "charges", label: "CHARGES", sortable: false },
    { key: "soa_billing_invoice", label: "SOA/BILLING INVOICE", sortable: false },
    { key: "date_billed", label: "DATE BILLED", sortable: true, type: "date" },
    { key: "amount_billed", label: "AMOUNT BILLED", sortable: true, type: "currency" },
    { key: "or_ar", label: "OR/AR", sortable: false },
    { key: "amount_collected", label: "AMOUNT COLLECTED", sortable: true, type: "currency" },
    { key: "bank_deposited", label: "BANK DEPOSITED", sortable: false },
    { key: "workflow_documents", label: "DOCS", sortable: false },
  ];

  const renderCellValue = (row, col) => {
    const value = row[col.key];
    if (col.type === "currency") return formatCurrency(value);
    if (col.type === "percentage") return formatPercentage(value);
    if (col.type === "date") return formatDate(value);
    return value || "—";
  };

  return (
    <div className="p-6 font-poppins">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-blue mb-2">Production Report</h1>
        <p className="text-grey text-sm">
          Consolidated view of job orders with cost analysis, billing, and liquidation data
        </p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-grey mb-1">Total Records</p>
            <p className="text-xl font-bold text-blue">{summary.count || 0}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-grey mb-1">Total Billing</p>
            <p className="text-xl font-bold text-blue">{formatCurrency(summary.totalBillingCharges)}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-grey mb-1">Total Costs</p>
            <p className="text-xl font-bold text-red">{formatCurrency(summary.totalActualCosts)}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-grey mb-1">Total Gross Profit</p>
            <p className="text-xl font-bold text-green-600">{formatCurrency(summary.totalGrossProfit)}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-grey mb-1">Avg Cost %</p>
            <p className="text-xl font-bold text-blue">{formatPercentage(summary.overallCostPercentage)}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-grey mb-1">Avg Profit %</p>
            <p className="text-xl font-bold text-green-600">{formatPercentage(summary.overallGrossProfitPercentage)}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 mb-6">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[160px_minmax(0,1fr)_minmax(0,1fr)_auto_auto] lg:items-end">
          {/* Date Filter */}
          <div>
            <label className="block text-xs text-grey mb-1">Date Filter</label>
            <select
              value={dateFilter}
              onChange={(e) => {
                const nextValue = e.target.value;
                previousDateFilterRef.current = dateFilter;
                setDateFilter(nextValue);
                if (nextValue === "custom") {
                  openCustomDateModal();
                }
              }}
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue/20 focus:border-blue outline-none"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Client Name Filter */}
          <div>
            <label className="block text-xs text-grey mb-1">Client Name</label>
            <input
              type="text"
              value={clientNameFilter}
              onChange={(e) => setClientNameFilter(e.target.value)}
              placeholder="Search client..."
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue/20 focus:border-blue outline-none"
            />
          </div>

          {/* Job Order Filter */}
          <div>
            <label className="block text-xs text-grey mb-1">Job Order</label>
            <input
              type="text"
              value={jobOrderFilter}
              onChange={(e) => setJobOrderFilter(e.target.value)}
              placeholder="Search JO..."
              className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue/20 focus:border-blue outline-none"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-end gap-3 lg:justify-self-end">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="inline-flex min-w-[120px] items-center justify-center gap-2 px-5 py-3 bg-blue text-white rounded-xl text-sm font-semibold hover:bg-blue/90 transition-colors disabled:opacity-50 shadow-sm"
            >
              <i className="fa-solid fa-search mr-2"></i>
              Search
            </button>
            <button
              onClick={handleReset}
              className="inline-flex min-w-[120px] items-center justify-center gap-2 px-5 py-3 bg-gray-100 text-grey rounded-xl text-sm font-semibold hover:bg-gray-200 transition-colors shadow-sm"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {customDateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <div className="mb-4">
              <h2 className="text-lg font-bold text-blue">Custom Date Range</h2>
              <p className="text-sm text-grey">Choose the start and end date for the report.</p>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs text-grey mb-1">Start Date</label>
                <input
                  type="date"
                  value={customDateDraftStart}
                  onChange={(e) => setCustomDateDraftStart(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue/20 focus:border-blue outline-none"
                />
              </div>
              <div>
                <label className="block text-xs text-grey mb-1">End Date</label>
                <input
                  type="date"
                  value={customDateDraftEnd}
                  onChange={(e) => setCustomDateDraftEnd(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue/20 focus:border-blue outline-none"
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={closeCustomDateModal}
                className="inline-flex items-center justify-center rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-grey transition hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={applyCustomDateRange}
                disabled={!customDateDraftStart || !customDateDraftEnd}
                className="inline-flex items-center justify-center rounded-xl bg-blue px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue/90 disabled:opacity-50"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 text-sm text-grey">
        <span className="font-semibold text-slate-600">Active Date Range:</span>{" "}
        <span className="text-blue">{getActiveDateRangeLabel()}</span>
      </div>

      {/* Export Button */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-grey">
          Showing {sortedData.length} record{sortedData.length !== 1 ? "s" : ""}
        </p>
        <button
          onClick={exportToExcel}
          disabled={sortedData.length === 0}
          className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 transition-colors disabled:opacity-50"
        >
          <i className="fa-solid fa-file-excel mr-2"></i>
          Export to Excel
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red/10 border border-red/20 rounded-xl p-4 mb-4">
          <p className="text-red text-sm">{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-blue text-white">
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className={`px-3 py-3 text-left font-semibold whitespace-nowrap ${
                      col.sortable ? "cursor-pointer hover:bg-blue/80" : ""
                    }`}
                    onClick={() => col.sortable && handleSort(col.key)}
                  >
                    <div className="flex items-center gap-2">
                      <span>{col.label}</span>
                      {col.sortable && (
                        <i className={`fa-solid ${getSortIcon(col.key)} text-xs opacity-70`}></i>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-8 h-8 border-3 border-blue/20 border-t-blue rounded-full animate-spin"></div>
                      <p className="text-grey">Loading report data...</p>
                    </div>
                  </td>
                </tr>
              ) : sortedData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <i className="fa-solid fa-folder-open text-4xl text-gray-300"></i>
                      <p className="text-grey">No production report data found</p>
                      <p className="text-xs text-gray-400">
                        Try adjusting your filters or check if cost analysis reports exist
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                <>
                  {sortedData.map((row, idx) => (
                    <tr
                      key={row.request_id || idx}
                      className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                        idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                      }`}
                    >
                      {columns.map((col) => (
                        <td key={col.key} className="px-3 py-2 whitespace-nowrap">
                          {col.type === "currency" ? (
                            <span
                              className={
                                col.key === "gross_profit"
                                  ? Number(row[col.key] || 0) >= 0
                                    ? "text-green-600 font-medium"
                                    : "text-red font-medium"
                                  : ""
                              }
                            >
                              {renderCellValue(row, col)}
                            </span>
                          ) : col.key === "charges" ? (
                            <span
                              className="max-w-[150px] truncate block"
                              title={row.charges}
                            >
                              {row.charges || "—"}
                            </span>
                          ) : col.key === "workflow_documents" ? (
                            <button
                              type="button"
                              onClick={() => openWorkflowDocuments(row)}
                              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-slate-800"
                            >
                              <i className="fa-solid fa-folder-open" />
                              View Docs
                            </button>
                          ) : (
                            renderCellValue(row, col)
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {/* Totals Row */}
                  <tr className="bg-blue/5 font-semibold border-t-2 border-blue/20">
                    <td className="px-3 py-3" colSpan={3}>
                      TOTALS
                    </td>
                    <td className="px-3 py-3">{formatCurrency(totals.costRate)}</td>
                    <td className="px-3 py-3">{formatCurrency(totals.sellRate)}</td>
                    <td className="px-3 py-3 text-green-600">{formatCurrency(totals.grossProfit)}</td>
                    <td className="px-3 py-3">—</td>
                    <td className="px-3 py-3">—</td>
                    <td className="px-3 py-3">—</td>
                    <td className="px-3 py-3">—</td>
                    <td className="px-3 py-3">—</td>
                    <td className="px-3 py-3">—</td>
                    <td className="px-3 py-3">—</td>
                    <td className="px-3 py-3">—</td>
                    <td className="px-3 py-3">{formatCurrency(totals.amountBilled)}</td>
                    <td className="px-3 py-3">—</td>
                    <td className="px-3 py-3">{formatCurrency(totals.amountCollected)}</td>
                    <td className="px-3 py-3">—</td>
                    <td className="px-3 py-3">—</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {activeDocumentsRow && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 px-4 py-6">
          <div
            className="absolute inset-0"
            role="presentation"
            onClick={() => {
              setActiveDocumentsRow(null);
              setWorkflowDocs([]);
              setWorkflowDocsError("");
            }}
          />
          <div className="relative z-10 flex max-h-[92vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-6 py-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Workflow documents by JO
                </p>
                <h3 className="mt-1 text-xl font-bold text-slate-900">
                  {activeDocumentsRow.job_order_no || "—"}
                </h3>
                <p className="text-sm text-slate-500">
                  {activeDocumentsRow.client_name || "—"} | Request, liquidation, vouchers, and cost analysis
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setActiveDocumentsRow(null);
                  setWorkflowDocs([]);
                  setWorkflowDocsError("");
                }}
                className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className="border-b border-slate-100 bg-slate-50 px-6 py-3 text-sm text-slate-600">
              {workflowDocsLoading ? "Loading workflow documents..." : `${workflowDocs.length} document${workflowDocs.length === 1 ? "" : "s"} loaded`}
            </div>

            <div className="max-h-[calc(92vh-118px)] overflow-y-auto bg-slate-50 p-6">
              {workflowDocsLoading ? (
                <div className="flex min-h-[360px] items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-white">
                  <div className="flex flex-col items-center gap-3 text-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue/20 border-t-blue" />
                    <p className="text-sm text-slate-500">Loading request, liquidation, vouchers, and cost analysis...</p>
                  </div>
                </div>
              ) : workflowDocsError ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
                  {workflowDocsError}
                </div>
              ) : workflowDocs.length ? (
                <div className="grid gap-4 xl:grid-cols-2">
                  {workflowDocs.map((doc) => (
                    <div
                      key={doc.key}
                      className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue">
                              <i
                                className={`fa-solid ${
                                  doc.key === "request"
                                    ? "fa-file-lines"
                                    : doc.key === "cost-analysis"
                                      ? "fa-calculator"
                                      : doc.key === "liquidation"
                                        ? "fa-receipt"
                                        : "fa-file-invoice"
                                }`}
                              />
                            </div>
                            <div className="min-w-0">
                              <div className="truncate font-semibold text-slate-900">{doc.title}</div>
                              <div className="truncate text-xs text-slate-500">{doc.subtitle}</div>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => openPrintableHtml(doc.html)}
                          className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-blue-700"
                        >
                          Open
                        </button>
                      </div>
                      <div className="bg-slate-50 p-3">
                        <iframe
                          title={doc.title}
                          srcDoc={doc.html}
                          className="h-[560px] w-full rounded-xl border-0 bg-white"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
                  No workflow documents found for this job order.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionReport;
